import { FieldErrors } from 'react-hook-form';
import { AsYouType, getCountryCallingCode, type CountryCode } from 'libphonenumber-js/max';
import phoneMetadata from 'libphonenumber-js/metadata.max.json';
import { hasFlag } from 'country-flag-icons';
import * as Flags from 'country-flag-icons/react/3x2';
import { US_LOCALE } from 'project.config';
import phoneMasks from 'public/static/phoneMasks.json';
import { ProjectSettings } from 'shared/types/ProjectSettings';
import { Address } from '../types/types';

type FlagComponent = (typeof Flags)[keyof typeof Flags];

// Re-typed as a plain lookup map so indexing it by a dynamic region code doesn't trip the
// `import/namespace` lint rule, which can't statically validate computed access on a namespace import.
const FlagIcons: Record<string, FlagComponent> = Flags;

interface UsAddressCandidate {
  city?: string;
  entries?: number;
  secondary?: string;
  state?: string;
  street_line?: string;
  zipcode?: string;
}

interface InternationalAddressCandidate {
  entries?: number;
  address_text?: string;
}

interface Country {
  name: string;
  code: string;
  iso3?: string;
}

interface PhoneMaskConfig {
  mask: string;
  minLength: number;
  maxLength: number;
  allowLeadingZero: boolean;
}

interface PhoneMaskResultConfig {
  mask: string;
  allowLeadingZero: boolean;
}

interface AddressVerification {
  streetAddress1: string;
  apartment?: string;
  streetAddress2?: string;
  streetAddress3?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}
interface VerifyLocationResponse {
  result?: AddressVerification;
  isAddressMatched?: boolean;
  isValid?: boolean;
  error?: string;
  isApiFailed?: boolean;
}

interface SmartyAddressComponents {
  city?: string;
  state?: string;
  postalCode?: string;
}

interface VerifyAndSubmitAddressParams {
  formData: Address;
  streetAddressChangesEnabled: boolean;
  smartyKey: string | undefined;
  smartyAddressVerificationUrl: string | undefined;
  onSubmit: (address: Address) => Promise<boolean>;
  onAddressMismatch: (formData: Address, result: AddressVerification) => void;
  onValidationFailed: (formData: Address) => void;
  onApiFailed: () => void;
}

const SMARTY_STATUS_ERROR_BY_CODE: Record<number, string> = {
  400: 'Bad request (malformed payload)',
  401: 'Unauthorized',
  402: 'Payment required',
  413: 'Request entity too large',
  422: 'Unprocessable entity',
  429: 'Too many requests',
  504: 'Gateway timeout',
};

type AddressVerificationParams = Record<
  'streetAddress' | 'apartment' | 'city' | 'state' | 'postalCode' | 'country',
  string
>;

/**
 * This will return the localized country name against the provided country code
 * E.g. "France" -> "France" but "Germany" -> "Allemagne" for fr locale
 */
const getCountryName = (countryCode: string | undefined, locale: string): string => {
  if (!countryCode) return '';

  try {
    const countryName = new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode);

    return countryName ?? countryCode;
  } catch {
    // Guards against a missing/invalid locale (e.g. no locale cookie set yet)
    return countryCode;
  }
};

/**
 * Given a country code, this function looks up the corresponding country details (name, ISO2 code, and ISO3 code) from a provided list.
 */
const getSelectedCountryDetails = (country: string, countryListWithCodes: Country[]): Country => {
  const matchedCountry = countryListWithCodes.find((c) => c.code === country);
  return {
    name: matchedCountry?.name ?? country,
    code: matchedCountry?.code ?? country,
    iso3: matchedCountry?.iso3 ?? country,
  };
};

/**
 * Retrieves the appropriate phone format mask for a given country.
 * Defaults to US mask if the country code is missing or unsupported.
 */
const getPhoneMask = (countryCode: string): PhoneMaskResultConfig => {
  const masks: Record<string, PhoneMaskConfig> = phoneMasks;
  const config = masks[countryCode.toUpperCase()] || masks.US;
  return { mask: config.mask, allowLeadingZero: config.allowLeadingZero };
};

/**
 * Counts the digits present in a (possibly masked) phone value, including any fixed
 * calling-code digits (e.g. the "1" in "+1"), since minLength/maxLength are defined
 * per-country as the total digit count of the fully rendered mask.
 */
const getPhoneDigitCount = (value: string): number => {
  if (!value) return 0;

  return (value.match(/\d/g) || []).length;
};

// Validates a phone value with its own embedded calling code (e.g. "+1 567 543 4565").
const isValidInternationalPhoneNumber = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return false;

  const formatter = new AsYouType();
  formatter.input(`+${digits}`);
  return formatter.isValid();
};

// Returns the longest prefix of `source` that's a valid international number, or undefined if none is.
const findValidPhonePrefix = (source: string): string | undefined => {
  for (let length = source.length; length >= 1; length--) {
    const prefix = source.slice(0, length);
    const formatter = new AsYouType();
    formatter.input(`+${prefix}`);
    if (formatter.isValid()) return prefix;
  }
  return undefined;
};

/**
 * Safe last-resort trunk-prefix digit for calling codes not in the eager map below — most
 * countries' own metadata already handles this before we get here.
 */
const DEFAULT_TRUNK_PREFIX_DIGITS = new Set(['0']);

/**
 * Trunk digits never legitimate for their calling code (NANP's "+1" has no trunk prefix at all;
 * +7's "8" is the old USSR trunk digit) — stripped once another digit follows, not on validity
 * alone, since e.g. a Russian number can coincidentally look valid with the "8" still in it.
 */
const EAGER_TRUNK_PREFIX_DIGIT_BY_CALLING_CODE: Record<string, string> = {
  '1': '0',
  '7': '8',
};

const stripEagerLeadingTrunkDigit = (rawDigits: string, callingCode: string | undefined): string => {
  const trunkDigit = callingCode ? EAGER_TRUNK_PREFIX_DIGIT_BY_CALLING_CODE[callingCode] : undefined;
  if (!callingCode || !trunkDigit) return rawDigits;

  const trunkDigitIndex = callingCode.length;
  const hasMoreDigitsAfterIt = rawDigits.length > trunkDigitIndex + 1;

  return hasMoreDigitsAfterIt && rawDigits[trunkDigitIndex] === trunkDigit
    ? rawDigits.slice(0, trunkDigitIndex) + rawDigits.slice(trunkDigitIndex + 1)
    : rawDigits;
};

// Formats a full international number, capping over-typed digits and stripping/keeping trunk prefixes per each country's rules.
const getFormattedInternationalPhoneNumber = (rawValue: string): string => {
  const rawDigitsInput = rawValue.replace(/\D/g, '');
  if (!rawDigitsInput) return '';

  const callingCodeProbe = new AsYouType();
  callingCodeProbe.input(`+${rawDigitsInput}`);
  const callingCode = callingCodeProbe.getCallingCode();

  const rawDigits = stripEagerLeadingTrunkDigit(rawDigitsInput, callingCode);

  let digits = findValidPhonePrefix(rawDigits);

  // Retried whenever the raw digits aren't valid exactly as typed — even if trailing-digit
  // truncation above already found some other coincidentally-valid, shorter number — since
  // treating a leading trunk digit as spurious better preserves what the user actually typed.
  if (digits !== rawDigits) {
    const trunkDigit = callingCode ? rawDigits[callingCode.length] : undefined;

    if (callingCode && trunkDigit && DEFAULT_TRUNK_PREFIX_DIGITS.has(trunkDigit)) {
      const withoutTrunkPrefix = rawDigits.slice(0, callingCode.length) + rawDigits.slice(callingCode.length + 1);
      digits = findValidPhonePrefix(withoutTrunkPrefix) ?? digits;
    }
  }

  digits ??= rawDigits;

  const probe = new AsYouType();
  probe.input(`+${digits}`);
  const canonicalNumber = probe.getNumber()?.number;
  if (canonicalNumber) digits = canonicalNumber.replace(/\D/g, '');

  const formatter = new AsYouType();
  return formatter.input(`+${digits}`);
};

/**
 * A calling code can be shared by several countries (e.g. +1 -> US, Canada...); metadata lists the
 * "main" one first (e.g. US for +1), used as the flag's fallback.
 */
const getRegionForCallingCode = (callingCode: string): CountryCode | undefined =>
  (phoneMetadata.country_calling_codes as Record<string, CountryCode[]>)[callingCode]?.[0];

// Returns the calling code for an ISO2 country (e.g. "US" -> "1"), or '' if unrecognized.
const getCallingCodeForCountry = (isoCountryCode: string): string => {
  try {
    return getCountryCallingCode(isoCountryCode as CountryCode);
  } catch {
    return '';
  }
};

// Determines the appropriate phone region code for displaying the correct flag based on the entered phone number.
const getPhoneRegionCodeForFlag = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';

  const formatter = new AsYouType();
  formatter.input(`+${digits}`);
  const callingCode = formatter.getCallingCode();
  const region = formatter.getCountry() ?? (callingCode ? getRegionForCallingCode(callingCode) : undefined);
  return region || '';
};

// Resolves the flag icon component for the phone number's current region, or undefined if none exists.
const getPhoneFlagComponent = (value: string): FlagComponent | undefined => {
  const regionCode = getPhoneRegionCodeForFlag(value);
  return regionCode && hasFlag(regionCode) ? FlagIcons[regionCode] : undefined;
};

// True once the value has digits beyond its own embedded calling code (a real number was typed),
const hasEnteredPhoneNationalNumber = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return false;

  const formatter = new AsYouType();
  formatter.input(`+${digits}`);
  const callingCode = formatter.getCallingCode();
  return !callingCode || digits.length > callingCode.length;
};

// True when a leading "0" is a trunk prefix to drop (per the mask), not a genuine digit (Italy).
const isLeadingTrunkZero = (phoneMask: PhoneMaskResultConfig, isFirstPlaceholder: boolean, digit?: string): boolean =>
  !phoneMask.allowLeadingZero && isFirstPlaceholder && digit === '0';

/**
 * Transforms a raw string of digits into a formatted phone number based on a mask.
 * Supports '#' or '_' as placeholders for numeric digits.
 * When maxLength is provided, digits beyond it are truncated regardless of mask length.
 */
const applyMask = (value: string, phoneMask: PhoneMaskResultConfig, maxLength?: number): string => {
  if (!value) return '';

  // Clean EVERYTHING except digits
  const digits = value.replace(/\D/g, '').slice(0, maxLength);

  let maskedValue = '';
  let digitIndex = 0;
  let isFirstPlaceholder = true;

  for (let i = 0; i < phoneMask.mask.length && digitIndex < digits.length; i++) {
    if (phoneMask.mask[i] === '_' || phoneMask.mask[i] === '#') {
      if (isLeadingTrunkZero(phoneMask, isFirstPlaceholder, digits[digitIndex])) {
        digitIndex++;
      }
      isFirstPlaceholder = false;

      if (digitIndex >= digits.length) break;

      maskedValue += digits[digitIndex];
      digitIndex++;
    } else {
      // If the mask has a fixed char (like +, (, or space), add it automatically
      maskedValue += phoneMask.mask[i];

      // If the user typed the fixed char (like the +), skip it in our digit count
      if (digits[digitIndex] === phoneMask.mask[i]) {
        digitIndex++;
      }
    }
  }

  return maskedValue;
};

/**
 * Fetches real-time address predictions using the Smarty US/International Autocomplete Pro API.
 * Returns up to 10 suggestions based on the user's partial input.
 */
const getAddressSuggestions = async (url: string, smartyKey: string, countryCode: string, search: string) => {
  const isUS = countryCode === US_LOCALE;

  const params = new URLSearchParams({
    key: smartyKey,
    search,
    max_results: '10',
  });

  if (!isUS) params.append('country', countryCode);

  try {
    const response = await fetch(`${url}?${params}`);

    if (!response.ok) {
      console.error(`Smarty Autocomplete Error (${response.status})`);
      return [];
    }

    const data = await response.json();

    if (isUS) {
      const addresses: UsAddressCandidate[] = data?.suggestions || [];
      return addresses.filter((address) => address.entries === 0) || [];
    }

    // For International, only return those addresses which has entries==1
    const addresses: InternationalAddressCandidate[] = data?.candidates || [];
    return addresses.filter((address) => address.entries === 1) || [];
  } catch (error) {
    console.error('Autocomplete request failed:', error);
    return [];
  }
};

/**
 * Fetches detailed address
 * Specially for CA and EU region
 */
const getDetailedAddress = async (url: string, smartyKey: string, countryCode: string, addressId: string) => {
  const params = new URLSearchParams({
    key: smartyKey,
    country: countryCode,
  });

  const urlWithParams = `${url}/${addressId}?${params}`;

  try {
    const response = await fetch(urlWithParams);

    if (!response.ok) {
      console.error(`Smarty Detailed Address Error (${response.status})`);
      return [];
    }

    const data = await response.json();

    return data?.candidates || [];
  } catch (error) {
    console.error('Detailed address request failed:', error);
    return [];
  }
};

// Retrieves the appropriate Smarty URLs based on the region and project settings.
const getSmartyUrls = (
  region: string,
  projectSettings: ProjectSettings | undefined,
  enableStreetAddressChanges: boolean,
) => {
  const isUS = region === US_LOCALE;

  let smartyAddressVerificationUrl: string | undefined;

  if (enableStreetAddressChanges) {
    smartyAddressVerificationUrl = isUS
      ? projectSettings?.smartyUsAddressVerificationUrl
      : projectSettings?.smartyInternationalAddressVerificationUrl;
  } else {
    smartyAddressVerificationUrl = isUS
      ? projectSettings?.smartyUsZipCodeUrl
      : projectSettings?.smartyInternationalZipCodeUrl;
  }

  return {
    smartyKey: projectSettings?.smartyKey,
    smartyAddressVerificationUrl,
  };
};

// Compares two addresses and returns true if they match
const compareAddresses = (enteredAddress: AddressVerification, smartyAddress: AddressVerification) => {
  const smartyStreetAddress1 = smartyAddress.streetAddress1.replaceAll(',', '').toLowerCase();
  const enteredStreetAddress1 = enteredAddress.streetAddress1.replaceAll(',', '').toLowerCase();
  const smartyStreetAddress = [
    smartyAddress.streetAddress1,
    smartyAddress.streetAddress2,
    smartyAddress?.streetAddress3,
  ]
    .filter(Boolean)
    .join(' ')
    .replaceAll(',', '')
    .toLowerCase()
    .trim();

  return (
    ((enteredStreetAddress1 === smartyStreetAddress1 &&
      !smartyAddress?.streetAddress2?.toLocaleLowerCase() &&
      !smartyAddress?.streetAddress3?.toLocaleLowerCase()) ||
      enteredStreetAddress1 === smartyStreetAddress) &&
    enteredAddress.apartment?.toLocaleLowerCase() === smartyAddress.apartment?.toLocaleLowerCase() &&
    enteredAddress.city.toLocaleLowerCase() === smartyAddress.city.toLocaleLowerCase() &&
    enteredAddress.state.toLocaleLowerCase() === smartyAddress.state.toLocaleLowerCase() &&
    enteredAddress.postalCode.toLocaleLowerCase() === smartyAddress.postalCode.toLocaleLowerCase() &&
    enteredAddress.country.toLocaleLowerCase() === smartyAddress.country.toLocaleLowerCase()
  );
};

// Extracts the distinct part of an address line that is not echoed back by Smarty's components,
// nor already present in streetAddress1 (e.g. a venue/centre name Smarty repeats on both lines).
const getDistinctAddressLine = (
  line: string | undefined,
  components: SmartyAddressComponents,
  streetAddress1 = '',
): string => {
  if (!line) return '';

  const echoedValues = [components.city, components.state, components.postalCode]
    .filter((value): value is string => Boolean(value))
    .sort((a, b) => b.length - a.length);

  const remainder = echoedValues.reduce((text, value) => text.split(value).join(''), line).replace(/[\s,.-]/g, '');

  if (!remainder) return '';

  const normalize = (value: string) => value.replace(/[\s,.-]/g, '').toLowerCase();
  if (normalize(streetAddress1).includes(normalize(line))) return '';

  return line;
};

// Smarty's international API can return address lines up to address12.
const MAX_SMARTY_ADDRESS_LINE_INDEX = 12;

// Combines every address line from `startIndex` onward that isn't just an echo of city/state/postal
// code or streetAddress1 itself.
const combineDistinctAddressLines = (
  result: Record<string, unknown>,
  startIndex: number,
  components: SmartyAddressComponents,
  streetAddress1: string,
): string => {
  const lines: string[] = [];

  for (let i = startIndex; i <= MAX_SMARTY_ADDRESS_LINE_INDEX; i++) {
    const line = result[`delivery_line_${i}`] || result[`address${i}`];
    const distinctLine = typeof line === 'string' ? getDistinctAddressLine(line, components, streetAddress1) : '';
    if (distinctLine) lines.push(distinctLine);
  }

  return lines.join(', ');
};

/**
 * Validates if a City, State, and ZIP Code combination is correct.
 * Utilizes the Smarty US ZIP Code API for verification.
 */
const verifyLocation = async (
  enableStreetAddressChanges: boolean,
  url: string,
  smartyKey: string,
  street: string,
  apartment: string,
  city: string,
  state: string,
  postalCode: string,
  country: string,
): Promise<VerifyLocationResponse> => {
  const urlWithParams = new URL(url);

  urlWithParams.searchParams.append('key', smartyKey);

  if (country === US_LOCALE) {
    if (enableStreetAddressChanges) urlWithParams.searchParams.append('street', street);
    // if (enableStreetAddressChanges) urlWithParams.searchParams.append('secondary', apartment);
    urlWithParams.searchParams.append('city', city);
    urlWithParams.searchParams.append('state', state);
    urlWithParams.searchParams.append('zipcode', postalCode);
  } else {
    if (enableStreetAddressChanges) urlWithParams.searchParams.append('address1', street);
    // if (enableStreetAddressChanges) urlWithParams.searchParams.append('address2', apartment);
    urlWithParams.searchParams.append('locality', city);
    urlWithParams.searchParams.append('administrative_area', state);
    urlWithParams.searchParams.append('postal_code', postalCode);
    urlWithParams.searchParams.append('country', country);
  }

  try {
    const response = await fetch(urlWithParams.toString());

    if (!response.ok) {
      return {
        isValid: false,
        isApiFailed: true,
        error: SMARTY_STATUS_ERROR_BY_CODE[response.status] ?? 'Network error',
      };
    }

    const data = await response.json();

    if (!Array.isArray(data) || data.length === 0) {
      return { isValid: false, error: 'Adddress not verified by Smarty' };
    }

    const result = data[0];

    if (result.status) {
      return { isValid: false, error: `Address validation failed due to: ${result.status}` };
    }

    const components: SmartyAddressComponents = {
      city: result.components?.city_name || result.components?.locality || '',
      state: result.components?.state_abbreviation || result.components?.administrative_area || '',
      postalCode: result.components?.zipcode || result.components?.postal_code || '',
    };

    const resStreetAddress1 =
      result.delivery_line_1 ||
      (result.organization ? `${result.organization} ${result.address1}` : result.address1) ||
      '';

    const resStreetAddress2 = getDistinctAddressLine(
      result.delivery_line_2 || result.address2 || '',
      components,
      resStreetAddress1,
    );
    const resStreetAddress3 = combineDistinctAddressLines(result, 3, components, resStreetAddress1);

    const resCity = enableStreetAddressChanges
      ? components.city || ''
      : result.city_states?.[0]?.city || result.locality || '';

    const resState = enableStreetAddressChanges
      ? components.state || ''
      : result.city_states?.[0]?.state_abbreviation || result.administrative_area || '';

    const resPostalCode = enableStreetAddressChanges
      ? components.postalCode || ''
      : result.zipcodes?.[0]?.zipcode || result.postal_code || '';

    // User entered address
    const enteredAddress = {
      streetAddress1: street,
      streetAddress2: '',
      streetAddress3: '',
      apartment,
      city,
      state,
      postalCode,
      country,
    };

    // Smarty returned address
    const smartyAddress = {
      streetAddress1: resStreetAddress1,
      streetAddress2: resStreetAddress2,
      streetAddress3: resStreetAddress3,
      apartment,
      city: resCity,
      state: resState,
      postalCode: resPostalCode,
      country,
    };

    // compare the user entered address with the smarty returned address
    const isAddressMatched = compareAddresses(enteredAddress, smartyAddress);

    return {
      isValid: true,
      isAddressMatched,
      result: smartyAddress,
    };
  } catch (error) {
    console.error('Validation request failed:', error);
    return { isValid: false, isApiFailed: true, error: 'Validation request failed' };
  }
};

// Caches the last successful Smarty response so re-submitting the same, unchanged address
// (e.g. after denying the verification modal) reuses it instead of calling Smarty again.
let verificationCache: { params: AddressVerificationParams; response: VerifyLocationResponse } | null = null;

/**
 * Verifies an address via Smarty, reusing the cached response when the address fields are
 * unchanged from the last call (e.g. re-submitting after denying the verification modal).
 */
const verifyLocationWithCache = async (
  enableStreetAddressChanges: boolean,
  url: string,
  smartyKey: string,
  verificationParams: AddressVerificationParams,
): Promise<VerifyLocationResponse> => {
  const cached = verificationCache;
  const isCacheHit =
    !!cached &&
    (Object.keys(verificationParams) as (keyof AddressVerificationParams)[]).every(
      (key) => cached.params[key] === verificationParams[key],
    );

  if (isCacheHit) return cached.response;

  const response = await verifyLocation(
    enableStreetAddressChanges,
    url,
    smartyKey,
    verificationParams.streetAddress,
    verificationParams.apartment,
    verificationParams.city,
    verificationParams.state,
    verificationParams.postalCode,
    verificationParams.country,
  );

  if ((enableStreetAddressChanges && !response.isApiFailed) || (!enableStreetAddressChanges && response.isValid)) {
    verificationCache = { params: verificationParams, response };
  }

  return response;
};

// Combines Smarty's secondary/tertiary street lines into the single line.
const combineAddressLines = (address: Pick<AddressVerification, 'streetAddress2' | 'streetAddress3'>): string =>
  [address.streetAddress2, address.streetAddress3].filter(Boolean).join(', ');

/**
 * Shared Smarty-verify-then-submit flow used by both the account "add/edit address" form and
 * the checkout shipping/billing address form: verifies the address (with caching), applies
 * Smarty's corrected fields, and delegates to the caller's `onSubmit` once verification passes.
 */
const verifyAndSubmitAddress = async ({
  formData,
  streetAddressChangesEnabled,
  smartyKey,
  smartyAddressVerificationUrl,
  onSubmit,
  onAddressMismatch,
  onValidationFailed,
  onApiFailed,
}: VerifyAndSubmitAddressParams): Promise<boolean> => {
  if (!smartyKey || !smartyAddressVerificationUrl) {
    console.error('Smarty is not configured');
    await onSubmit(formData);
    return true;
  }

  const { result, isValid, isAddressMatched, error, isApiFailed } = await verifyLocationWithCache(
    streetAddressChangesEnabled,
    smartyAddressVerificationUrl,
    smartyKey,
    {
      streetAddress: formData.streetAddress ?? '',
      apartment: formData.apartment ?? '',
      city: formData.city ?? '',
      state: formData.state ?? '',
      postalCode: formData.postalCode ?? '',
      country: formData.country ?? '',
    },
  );

  if (!isValid) {
    if (isApiFailed) {
      onApiFailed();
      return false;
    }

    onValidationFailed(formData);
    console.error(error);
    return false;
  }

  if (streetAddressChangesEnabled) {
    if (result) {
      if (!isAddressMatched) {
        onAddressMismatch(formData, result);
        return false;
      }
      formData.streetAddress = result.streetAddress1;
      formData.apartment = result.apartment;
      formData.additionalAddressInfo = combineAddressLines(result);
    }
  } else if (result) {
    formData.city = result.city ?? formData.city;
    formData.state = result.state ?? formData.state;
    formData.postalCode = result.postalCode ?? formData.postalCode;
  }

  return await onSubmit(formData);
};

/**
 * A Studio-authored input rule, written as `[allowed characters]{min,max}` — e.g. `[A-Za-z0-9 #.,'/-]{0,30}`.
 * Restricting editors to one character class plus a length range keeps the pattern unambiguous and
 * linear-time, and means a `]` can never escape the class to change what the rule matches.
 */
interface FieldCharacterRule {
  disallowedCharacters: RegExp;
  minLength: number;
  maxLength: number;
}

const FIELD_CHARACTER_RULE_PATTERN = /^\[([^\]]+)\]\{(\d+),(\d+)\}$/;

const DEFAULT_APT_STE_RULE = "[A-Za-z0-9 #.,'/-]{0,30}";
const DEFAULT_STREET_ADDRESS_RULE = "[A-Za-z0-9 #.,'/-]{0,100}";

const compileFieldCharacterRule = (rule: string): FieldCharacterRule | undefined => {
  const match = FIELD_CHARACTER_RULE_PATTERN.exec(rule.trim());
  if (!match) return undefined;

  const [, allowedCharacters, minLength, maxLength] = match;
  if (Number(maxLength) < Number(minLength)) return undefined;

  try {
    return {
      disallowedCharacters: new RegExp(`[^${allowedCharacters}]`, 'gu'),
      minLength: Number(minLength),
      maxLength: Number(maxLength),
    };
  } catch {
    // Ranges like [z-a] pass the shape check but still fail to compile.
    return undefined;
  }
};

/**
 * Falls back to the default rule whenever Studio's value is missing or malformed, so a bad edit
 * narrows to a sensible default instead of leaving the field unvalidated.
 */
const parseFieldCharacterRule = (rule: string | undefined, defaultRule: string): FieldCharacterRule => {
  const compiledRule = rule ? compileFieldCharacterRule(rule) : undefined;
  if (compiledRule) return compiledRule;

  if (rule) console.warn(`Ignoring malformed field rule "${rule}"; falling back to "${defaultRule}".`);

  return compileFieldCharacterRule(defaultRule) as FieldCharacterRule;
};

const DURATION_TO_SHOW_TOAST = 2000;

// Delay (ms) before centering on the errored field, so it runs after Input's own focus-triggered scroll settles.
const SCROLL_TO_ERROR_DELAY = 150;

// Focuses and smoothly centers the topmost field with a validation error, when there are multiple.
const scrollToFirstErrorField = (errors: FieldErrors): void => {
  const errorFieldNames = Object.keys(errors);
  if (errorFieldNames.length === 0) return;

  const errorFields = Array.from(document.querySelectorAll<HTMLElement>('[name]')).filter((field) =>
    errorFieldNames.includes(field.getAttribute('name') || ''),
  );

  const [firstErrorField] = errorFields.toSorted(
    (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
  );
  if (!firstErrorField) return;

  firstErrorField.focus({ preventScroll: true });
  window.setTimeout(() => {
    firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, SCROLL_TO_ERROR_DELAY);
};

export type { FlagComponent, AddressVerification, AddressVerificationParams, FieldCharacterRule };

export {
  getCountryName,
  getSelectedCountryDetails,
  getPhoneMask,
  getPhoneDigitCount,
  isValidInternationalPhoneNumber,
  getFormattedInternationalPhoneNumber,
  getPhoneRegionCodeForFlag,
  getPhoneFlagComponent,
  hasEnteredPhoneNationalNumber,
  getCallingCodeForCountry,
  applyMask,
  getAddressSuggestions,
  verifyLocation,
  verifyLocationWithCache,
  verifyAndSubmitAddress,
  combineAddressLines,
  getDetailedAddress,
  parseFieldCharacterRule,
  DURATION_TO_SHOW_TOAST,
  scrollToFirstErrorField,
  getSmartyUrls,
  DEFAULT_APT_STE_RULE,
  DEFAULT_STREET_ADDRESS_RULE,
};
